/**
 * UserDetailPage Component
 *
 * Administration page for viewing and managing a single user's account.
 * Provides:
 * - User profile information
 * - Subscription status and history
 * - Account management actions (enable/disable, change subscription, unlock)
 *
 * @validates Requirements 12.1, 12.2, 12.3, 12.4, 12.5, 12.6
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useStore } from '../../store';
import { SubscriptionType } from '@spellion/shared';

// ============================================================================
// Types
// ============================================================================

interface UserDetail {
  id: string;
  email: string;
  role: string;
  subscriptionType: SubscriptionType;
  isActive: boolean;
  isLocked?: boolean;
  lockTTL?: number | null;
  lockReason?: 'login' | '2fa' | null;
  paymentType?: 'subscription' | 'one-time' | null;
  subscriptionEndDate?: string | null;
  subscriptionStartDate?: string | null;
  createdAt: string;
  lastLogin: string | null;
  timezone: string;
}

interface SubscriptionHistoryEntry {
  id: string;
  userId: string;
  subscriptionStartDate: string;
  subscriptionEndDate: string;
  paymentType: 'subscription' | 'one-time' | null;
  amountPaid: number;
  paypalSubscriptionId: string | null;
  paypalOrderId: string | null;
  status: 'active' | 'completed' | 'cancelled' | 'expired';
  endedReason: 'expiration' | 'cancelled_by_user' | 'cancelled_by_admin' | 'payment_failed' | null;
  createdAt: string;
}

interface SubscriptionStats {
  totalSubscriptions: number;
  totalSpent: number;
  firstSubscriptionDate: string | null;
  lastSubscriptionDate: string | null;
}

interface ConfirmationState {
  isOpen: boolean;
  type: 'enable' | 'disable' | 'unlock' | 'change-subscription' | 'reset-password' | null;
  targetSubscription?: SubscriptionType;
}

// ============================================================================
// Constants
// ============================================================================

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// ============================================================================
// Icons
// ============================================================================

const ArrowLeftIcon = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
  </svg>
);

const UserIcon = ({ className = 'w-6 h-6' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const HistoryIcon = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const LockIcon = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
  </svg>
);

// ============================================================================
// Component
// ============================================================================

export function UserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const token = useStore((state) => state.token);

  // State
  const [user, setUser] = useState<UserDetail | null>(null);
  const [history, setHistory] = useState<SubscriptionHistoryEntry[]>([]);
  const [stats, setStats] = useState<SubscriptionStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [confirmation, setConfirmation] = useState<ConfirmationState>({
    isOpen: false,
    type: null,
  });

  // Build headers
  const getHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  }), [token]);

  // Format date
  const formatShortDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Format date with time for history
  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Fetch user details
  const fetchUser = useCallback(async () => {
    if (!userId) return;
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
        method: 'GET',
        headers: getHeaders(),
      });

      const data = await response.json();
      if (data.success) {
        setUser(data.data);
      } else {
        setError(data.error?.message || 'Erreur lors du chargement');
      }
    } catch (err) {
      setError('Erreur de connexion');
    } finally {
      setIsLoading(false);
    }
  }, [userId, getHeaders]);

  // Fetch subscription history
  const fetchHistory = useCallback(async () => {
    if (!userId) return;
    setHistoryLoading(true);

    try {
      const response = await fetch(
        `${API_BASE_URL}/admin/users/${userId}/subscription-history?limit=50`,
        { method: 'GET', headers: getHeaders() }
      );

      const data = await response.json();
      if (data.success) {
        setHistory(data.data || []);
        if (data.stats) {
          setStats(data.stats);
        }
      }
    } catch (err) {
      console.error('Failed to fetch subscription history:', err);
    } finally {
      setHistoryLoading(false);
    }
  }, [userId, getHeaders]);

  // Load data on mount
  useEffect(() => {
    fetchUser();
    fetchHistory();
  }, [fetchUser, fetchHistory]);

  // Handle status change (enable/disable)
  const handleStatusChange = async () => {
    if (!user || !confirmation.type) return;
    setActionLoading(true);

    try {
      const isActive = confirmation.type === 'enable';
      const response = await fetch(`${API_BASE_URL}/admin/users/${user.id}/status`, {
        method: 'PATCH',
        headers: getHeaders(),
        body: JSON.stringify({ isActive }),
      });

      const data = await response.json();
      if (data.success) {
        setUser({ ...user, isActive });
      }
    } catch (err) {
      console.error('Failed to change status:', err);
    } finally {
      setActionLoading(false);
      setConfirmation({ isOpen: false, type: null });
    }
  };

  // Handle unlock
  const handleUnlock = async () => {
    if (!user) return;
    setActionLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/admin/users/${user.id}/unlock`, {
        method: 'POST',
        headers: getHeaders(),
      });

      const data = await response.json();
      if (data.success) {
        setUser({ ...user, isLocked: false, lockTTL: null, lockReason: null });
      }
    } catch (err) {
      console.error('Failed to unlock:', err);
    } finally {
      setActionLoading(false);
      setConfirmation({ isOpen: false, type: null });
    }
  };

  // Handle subscription change
  const handleSubscriptionChange = async () => {
    if (!user || !confirmation.targetSubscription) return;
    setActionLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/admin/users/${user.id}/subscription`, {
        method: 'PATCH',
        headers: getHeaders(),
        body: JSON.stringify({ subscriptionType: confirmation.targetSubscription }),
      });

      const data = await response.json();
      if (data.success) {
        setUser({ ...user, subscriptionType: confirmation.targetSubscription });
      }
    } catch (err) {
      console.error('Failed to change subscription:', err);
    } finally {
      setActionLoading(false);
      setConfirmation({ isOpen: false, type: null });
    }
  };

  // Handle password reset
  const handlePasswordReset = async () => {
    if (!user) return;
    setActionLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/admin/users/${user.id}/reset-password`, {
        method: 'POST',
        headers: getHeaders(),
      });

      const data = await response.json();
      if (data.success) {
        // Optionally show success message
        console.log('Password reset email sent');
      }
    } catch (err) {
      console.error('Failed to reset password:', err);
    } finally {
      setActionLoading(false);
      setConfirmation({ isOpen: false, type: null });
    }
  };

  // Confirm action
  const confirmAction = () => {
    if (confirmation.type === 'enable' || confirmation.type === 'disable') {
      handleStatusChange();
    } else if (confirmation.type === 'unlock') {
      handleUnlock();
    } else if (confirmation.type === 'change-subscription') {
      handleSubscriptionChange();
    } else if (confirmation.type === 'reset-password') {
      handlePasswordReset();
    }
  };

  // Get status badge
  const getStatusBadge = (status: string, endedReason: string | null) => {
    switch (status) {
      case 'active':
        return { style: 'bg-success-100 text-success-700', label: '✓ Actif' };
      case 'expired':
        return { style: 'bg-slate-100 text-slate-600', label: '⏱ Expiré' };
      case 'cancelled':
        const reason = endedReason === 'cancelled_by_admin' ? ' (admin)' : ' (utilisateur)';
        return { style: 'bg-orange-100 text-orange-700', label: `✕ Annulé${reason}` };
      case 'completed':
        return { style: 'bg-blue-100 text-blue-700', label: '✓ Terminé' };
      default:
        return { style: 'bg-slate-100 text-slate-500', label: status };
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex items-center gap-3">
          <svg className="w-6 h-6 animate-spin text-primary-500" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <span className="text-slate-500">Chargement...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !user) {
    return (
      <div className="text-center py-12">
        <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-xl inline-block">
          <p>{error || 'Utilisateur non trouvé'}</p>
        </div>
        <div className="mt-6">
          <Link
            to="/admin/users"
            className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Retour à la liste
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header with back button */}
      <div className="flex items-center gap-4 mb-6">
        <button
          type="button"
          onClick={() => navigate('/admin/users')}
          className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
        >
          <ArrowLeftIcon className="w-5 h-5 text-slate-600" />
        </button>
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Détails utilisateur</h1>
          <p className="text-slate-500 text-sm mt-1">{user.email}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column: User info and actions */}
        <div className="lg:col-span-1 space-y-6">
          {/* Profile card */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center">
                <UserIcon className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="font-semibold text-lg text-slate-900">{user.email}</h2>
                <p className="text-sm text-slate-500">ID: {user.id}</p>
              </div>
            </div>

            <div className="space-y-4">
              {/* Subscription type */}
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-500">Abonnement</span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  user.subscriptionType === 'premium'
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-slate-100 text-slate-600'
                }`}>
                  {user.subscriptionType === 'premium' ? '⭐ Premium' : 'Gratuit'}
                </span>
              </div>

              {/* Status */}
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-500">Statut</span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  user.isActive
                    ? 'bg-success-100 text-success-700'
                    : 'bg-red-100 text-red-700'
                }`}>
                  {user.isActive ? '✓ Actif' : '✕ Désactivé'}
                </span>
              </div>

              {/* Lock status */}
              {user.isLocked && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-500">Verrouillage</span>
                  <span className="px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-700 flex items-center gap-1">
                    <LockIcon className="w-3.5 h-3.5" />
                    Verrouillé ({user.lockReason === '2fa' ? '2FA' : 'Login'})
                  </span>
                </div>
              )}

              {/* Payment type */}
              {user.paymentType && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-500">Type paiement</span>
                  <span className="text-sm text-slate-700">
                    {user.paymentType === 'subscription' ? '🔄 Récurrent' : '💳 Unique'}
                  </span>
                </div>
              )}

              {/* Expiration */}
              {user.subscriptionEndDate && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-500">Expiration</span>
                  <span className="text-sm text-slate-700">{formatShortDate(user.subscriptionEndDate)}</span>
                </div>
              )}

              {/* Member since */}
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-500">Inscrit le</span>
                <span className="text-sm text-slate-700">{formatShortDate(user.createdAt)}</span>
              </div>

              {/* Last login */}
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-500">Dernière connexion</span>
                <span className="text-sm text-slate-700">{formatShortDate(user.lastLogin)}</span>
              </div>

              {/* Timezone */}
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-500">Fuseau horaire</span>
                <span className="text-sm text-slate-700">{user.timezone || 'Europe/Paris'}</span>
              </div>
            </div>
          </div>

          {/* Actions card */}
          {user.role !== 'admin' && (
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Actions</h3>
              <div className="space-y-3">
                {/* Enable/Disable */}
                <button
                  type="button"
                  onClick={() => setConfirmation({
                    isOpen: true,
                    type: user.isActive ? 'disable' : 'enable',
                  })}
                  className={`w-full px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    user.isActive
                      ? 'bg-red-50 text-red-700 hover:bg-red-100'
                      : 'bg-success-50 text-success-700 hover:bg-success-100'
                  }`}
                >
                  {user.isActive ? 'Désactiver le compte' : 'Activer le compte'}
                </button>

                {/* Unlock */}
                {user.isLocked && (
                  <button
                    type="button"
                    onClick={() => setConfirmation({ isOpen: true, type: 'unlock' })}
                    className="w-full px-4 py-2.5 rounded-lg text-sm font-medium bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors"
                  >
                    Déverrouiller le compte
                  </button>
                )}

                {/* Change subscription - disabled if account is not active and trying to upgrade to premium */}
                <button
                  type="button"
                  onClick={() => setConfirmation({
                    isOpen: true,
                    type: 'change-subscription',
                    targetSubscription: user.subscriptionType === 'premium' ? 'free' as SubscriptionType : 'premium' as SubscriptionType,
                  })}
                  disabled={!user.isActive && user.subscriptionType !== 'premium'}
                  className={`w-full px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    !user.isActive && user.subscriptionType !== 'premium'
                      ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                      : 'bg-primary-50 text-primary-700 hover:bg-primary-100'
                  }`}
                  title={!user.isActive && user.subscriptionType !== 'premium' ? 'Activez le compte avant de passer en Premium' : undefined}
                >
                  Passer en {user.subscriptionType === 'premium' ? 'Gratuit' : 'Premium'}
                </button>

                {/* Reset password */}
                <button
                  type="button"
                  onClick={() => setConfirmation({ isOpen: true, type: 'reset-password' })}
                  className="w-full px-4 py-2.5 rounded-lg text-sm font-medium bg-slate-50 text-slate-700 hover:bg-slate-100 transition-colors"
                >
                  Réinitialiser le mot de passe
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Right column: Subscription history */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <HistoryIcon className="w-6 h-6 text-primary-600" />
                <h2 className="font-semibold text-lg text-slate-900">Historique des achats</h2>
                {stats && stats.totalSubscriptions > 0 && (
                  <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-primary-100 text-primary-700">
                    {stats.totalSubscriptions}
                  </span>
                )}
              </div>
            </div>

            {/* Stats summary */}
            {stats && stats.totalSubscriptions > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                <div className="bg-primary-50 rounded-xl p-4">
                  <p className="text-xs text-primary-600 uppercase tracking-wider font-medium">Total dépensé</p>
                  <p className="text-2xl font-bold text-primary-700 mt-1">{stats.totalSpent.toFixed(2)} €</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wider font-medium">Abonnements</p>
                  <p className="text-2xl font-bold text-slate-700 mt-1">{stats.totalSubscriptions}</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wider font-medium">Premier achat</p>
                  <p className="text-sm font-semibold text-slate-700 mt-1">{formatShortDate(stats.firstSubscriptionDate)}</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wider font-medium">Dernier achat</p>
                  <p className="text-sm font-semibold text-slate-700 mt-1">{formatShortDate(stats.lastSubscriptionDate)}</p>
                </div>
              </div>
            )}

            {/* History list */}
            {historyLoading ? (
              <div className="flex items-center justify-center py-12">
                <svg className="w-6 h-6 animate-spin text-primary-500" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              </div>
            ) : history.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <HistoryIcon className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>Aucun historique d'achat</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Période</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Type</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Montant</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Statut</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Créé le</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {history.map((entry) => {
                      const badge = getStatusBadge(entry.status, entry.endedReason);
                      return (
                        <tr key={entry.id} className="hover:bg-slate-50">
                          <td className="py-3 px-4">
                            <div className="text-sm text-slate-900">
                              {formatDateTime(entry.subscriptionStartDate)}
                            </div>
                            <div className="text-xs text-slate-500">
                              → {formatDateTime(entry.subscriptionEndDate)}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-sm text-slate-700">
                              {entry.paymentType === 'subscription' ? '🔄 Récurrent' : '💳 Unique'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-sm font-medium text-slate-900">{entry.amountPaid.toFixed(2)} €</span>
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${badge.style}`}>
                              {badge.label}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-slate-500">
                            {formatDateTime(entry.createdAt)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {confirmation.isOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              {confirmation.type === 'enable' && 'Activer le compte'}
              {confirmation.type === 'disable' && 'Désactiver le compte'}
              {confirmation.type === 'unlock' && 'Déverrouiller le compte'}
              {confirmation.type === 'change-subscription' && 'Changer l\'abonnement'}
              {confirmation.type === 'reset-password' && 'Réinitialiser le mot de passe'}
            </h3>
            <p className="text-slate-600 mb-6">
              {confirmation.type === 'enable' && `Voulez-vous activer le compte de ${user.email} ?`}
              {confirmation.type === 'disable' && `Voulez-vous désactiver le compte de ${user.email} ?`}
              {confirmation.type === 'unlock' && `Voulez-vous déverrouiller le compte de ${user.email} ?`}
              {confirmation.type === 'change-subscription' && `Voulez-vous passer ${user.email} en ${confirmation.targetSubscription === 'premium' ? 'Premium' : 'Gratuit'} ?`}
              {confirmation.type === 'reset-password' && `Un email de réinitialisation du mot de passe sera envoyé à ${user.email}.`}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setConfirmation({ isOpen: false, type: null })}
                className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors"
                disabled={actionLoading}
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={confirmAction}
                disabled={actionLoading}
                className={`px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors ${
                  confirmation.type === 'disable'
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-primary-600 hover:bg-primary-700'
                }`}
              >
                {actionLoading ? 'En cours...' : 'Confirmer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserDetailPage;
